import React, { memo, useEffect, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.scss';
import { Gallery, Item } from 'react-photoswipe-gallery';
import { useTranslation } from 'react-i18next';
import ContactsSection from '../Sections/ContactsSection';
import { prettyNumber } from '../../../utils/helpers';

const PhotosGallery = memo(
  ({ photos = [], residence = [], onClickHandler, phonesShown = [] }) => {
    const { t } = useTranslation();
    const [imagesSizesArr, setImagesSizesArr] = useState([]);
    const settings = {
      dots: false,
      infinite: false,
      arrows: true,
      speed: 500,
      slidesToShow: 1,
      slidesToScroll: 1
    };
    const imgAlt = t('sections.residences.img_alt', { name: residence.name });

    const setBackgroundImg = url => ({
      backgroundImage: `url(${url})`
    });

    const ItemsList =
      imagesSizesArr.length &&
      photos.map((photo, i) => {
        return (
          <Item
            original={photo.full}
            thumbnail={photo.f660x496}
            key={i}
            width={imagesSizesArr[i]?.width || 375}
            height={imagesSizesArr[i]?.height || 250}
            alt={i > 0 ? `${imgAlt} - ${i + 1}` : `${imgAlt}`}
          >
            {({ ref, open }) => (
              <div className="gallery__thumbnail">
                <div
                  className="gallery__bg"
                  ref={ref}
                  onClick={open}
                  onKeyPress={open}
                  role="button"
                  tabIndex="-1"
                />
                <div
                  className="gallery__thumbnail-img"
                  style={setBackgroundImg(photo.f660x496)}
                />
              </div>
            )}
          </Item>
        );
      });

    useEffect(() => {
      const loadImage = image => {
        return new Promise((resolve, reject) => {
          const loadImg = new Image();
          loadImg.src = image.f660x496;
          loadImg.onload = () => resolve(image.url);
          loadImg.onerror = err => reject(err);
        });
      };

      Promise.all(photos.map(image => loadImage(image)))
        .then(() => {
          const getImages = document.getElementsByClassName(
            'photo-gallery-hidden'
          );
          const imagesData = [];

          Array.from(getImages).forEach(item => {
            imagesData.push({ width: item.width, height: item.height });
          });

          setImagesSizesArr(imagesData);
        })
        .catch(err => console.log('Failed to load images', err));
    }, []);

    if (!photos?.length) {
      return null;
    }

    const prettyViews = useMemo(
      () => prettyNumber(residence.hits),
      [residence.hits]
    );

    const galleryOptions = {
      showHideOpacity: true,
      history: true,
      bgOpacity: 1,
      zoom: true
    };

    return (
      <div className="gallery__container">
        <div className="gallery__info-views">{prettyViews}</div>
        <div style={{ display: 'none' }}>
          {photos.map((photo, i) => (
            <img
              src={photo.f660x496}
              className="photo-gallery-hidden"
              key={i}
              alt=""
            />
          ))}
        </div>
        <Gallery options={galleryOptions}>
          <div className="gallery">
            <ContactsSection {...{ residence, onClickHandler, phonesShown }} />
            <div className="gallery__row">
              <Slider {...settings}>{ItemsList}</Slider>
            </div>
          </div>
        </Gallery>
      </div>
    );
  }
);

PhotosGallery.displayName = 'ItemPhotosGallery';

PhotosGallery.propTypes = {
  photos: PropTypes.array,
  residence: PropTypes.object,
  phonesShown: PropTypes.array,
  onClickHandler: PropTypes.func
};

export default PhotosGallery;
